import React from 'react'

import './css/TraceBackground.css'

// Reverse-time "shooting star" traces.
//
// Each trace is an orthogonal path (only horizontal and vertical segments)
// whose origin is a random point A. The animation plays backwards in time:
// the purple trajectory first fades in fully drawn, then a glowing point
// appears at the far end and travels back along the path to A, consuming the
// line as it goes. Its luminous tail points in the direction of travel (ahead
// of it, tapering toward A) like a meteor watched with time running in
// reverse. Reaching the origin, the point flares and vanishes. The picture is
// a trace being followed back to its source.
//
// Two modes: 'ambient' (the default) keeps a sparse, self-renewing
// population of traces running; 'click' draws nothing on its own and fires
// one trace per click inside the band, the point appearing under the cursor.
//
// Everything lives in CSS pixels; the canvas is scaled by the device pixel
// ratio once per resize.

interface Point {
	x: number
	y: number
}

interface RGB {
	r: number
	g: number
	b: number
}

type Phase = 'wait' | 'fadeIn' | 'travel' | 'vanish'

type Axis = 'h' | 'v'

export type TraceMode = 'click' | 'ambient'

export interface TraceBackgroundOptions {
	///@brief   'ambient': a self-renewing population. 'click': a trace per
	///         click, the point appearing where the visitor clicked.
	mode: TraceMode
	///@brief   Line, core of the point and origin marker.
	color: string
	///@brief   Aura around the point and its soft tail.
	glowColor: string
	///@brief   Concurrent traces per 100 000 CSS px² of canvas.
	density: number
	///@brief   Share of traces that are a single straight segment (only left/right
	///         or only up/down) rather than a staircase.
	pureAxisShare: number
	///@brief   Base travel speed of the point in CSS px per second.
	speed: number
	///@brief   Global multiplier on every alpha of the drawing, 1 being the
	///         full-strength look.
	opacity: number
}

const DEFAULT_OPTIONS: TraceBackgroundOptions = {
	mode: 'ambient',
	color: '#7A4A90',
	glowColor: '#C070E8',
	density: 0.6,
	pureAxisShare: 0.35,
	speed: 170,
	opacity: 0.6,
}

const MIN_TRACES: number = 2
const MAX_TRACES: number = 28
///@brief   Cap on simultaneous click-fired traces.
const MAX_CLICK_TRACES: number = 12
const EDGE_MARGIN: number = 24

function rand( min: number, max: number ): number {
	return min + Math.random() * ( max - min )
}

function clamp01( v: number ): number {
	return v < 0 ? 0 : v > 1 ? 1 : v
}

function easeOutCubic( t: number ): number {
	const u: number = 1 - t
	return 1 - u * u * u
}

// Mostly constant speed with a soft start from the far end and a soft arrival
// at the origin: a fully linear run looks mechanical, a full ease looks like a
// UI transition rather than something in flight.
function travelEase( t: number ): number {
	const smooth: number = t * t * ( 3 - 2 * t )
	return 0.6 * t + 0.4 * smooth
}

function parseColor( s: string, fallback: RGB ): RGB {
	const hex: RegExpExecArray | null = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})/i.exec( s.trim() )
	if ( hex !== null ) {
		return { r: parseInt( hex[ 1 ], 16 ), g: parseInt( hex[ 2 ], 16 ), b: parseInt( hex[ 3 ], 16 ) }
	}
	const rgb: RegExpExecArray | null = /^rgba?\s*\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)/i.exec( s.trim() )
	if ( rgb !== null ) {
		return { r: parseFloat( rgb[ 1 ] ), g: parseFloat( rgb[ 2 ] ), b: parseFloat( rgb[ 3 ] ) }
	}
	return fallback
}

function rgba( c: RGB, a: number ): string {
	return `rgba(${ c.r }, ${ c.g }, ${ c.b }, ${ a })`
}

function pathLength( pts: Point[] ): number {
	let length: number = 0
	for ( let i: number = 1; i < pts.length; i++ ) {
		length += Math.abs( pts[ i ].x - pts[ i - 1 ].x ) + Math.abs( pts[ i ].y - pts[ i - 1 ].y )
	}
	return length
}

// Builds an orthogonal path starting at `start` (or a random point). Each
// trace picks one horizontal direction (left or right) and one vertical
// direction (up or down) and never backtracks, so a staircase reads as a
// route rather than a scribble. Segments are shortened at the canvas edge; a
// path that ends up too short is rerolled with new directions.
function generatePath( w: number, h: number, pureAxisShare: number, start: Point | null = null ): Point[] {
	for ( let attempt: number = 0; attempt < 8; attempt++ ) {
		const pts: Point[] = []

		let x: number = start !== null ? start.x : rand( EDGE_MARGIN, w - EDGE_MARGIN )
		let y: number = start !== null ? start.y : rand( EDGE_MARGIN, h - EDGE_MARGIN )
		pts.push( { x, y } )

		const dirX: number = Math.random() < 0.5 ? -1 : 1
		const dirY: number = Math.random() < 0.5 ? -1 : 1

		const bPureAxis: boolean = Math.random() < pureAxisShare
		let axis: Axis = Math.random() < 0.5 ? 'h' : 'v'
		const nbSegments: number = bPureAxis ? 1 : 2 + Math.floor( Math.random() * 4 )

		for ( let i: number = 0; i < nbSegments; i++ ) {
			let len: number = bPureAxis ? rand( 140, 380 ) : rand( 40, 200 )

			if ( axis === 'h' ) {
				const room: number = dirX > 0 ? ( w - EDGE_MARGIN - x ) : ( x - EDGE_MARGIN )
				len = Math.min( len, room )
				if ( len < 24 ) break
				x += dirX * len
			} else {
				const room: number = dirY > 0 ? ( h - EDGE_MARGIN - y ) : ( y - EDGE_MARGIN )
				len = Math.min( len, room )
				if ( len < 24 ) break
				y += dirY * len
			}

			pts.push( { x, y } )
			axis = axis === 'h' ? 'v' : 'h'
		}

		if ( pts.length >= 2 && pathLength( pts ) >= 70 ) {
			return pts
		}
	}

	if ( start !== null ) {
		const dir: number = start.x > w * 0.5 ? -1 : 1
		return [ { x: start.x, y: start.y }, { x: start.x + dir * 80, y: start.y } ]
	}
	return [ { x: w * 0.5 - 40, y: h * 0.5 }, { x: w * 0.5 + 40, y: h * 0.5 } ]
}


class Trace {

	private points: Point[] = []
	///@brief   Arc length from the origin at each point of the path.
	private cumulative: number[] = []
	private length: number = 0

	private phase: Phase = 'wait'
	///@brief   Time spent in the current phase, in seconds.
	private t: number = 0

	private waitDuration: number = 0
	private fadeInDuration: number = 0.8
	private travelDuration: number = 1
	private vanishDuration: number = 0.5

	private trailLength: number = 100
	private headRadius: number = 2.4
	private auraRadius: number = 16

	///@brief   True for a click-fired trace: no wait, and the point shows up
	///         right away instead of once the trajectory has faded in.
	private bImmediate: boolean = false

	constructor( w: number, h: number, options: TraceBackgroundOptions, bStaggered: boolean, clickAt: Point | null = null ) {
		this.reset( w, h, options, bStaggered, clickAt )
	}

	// Rebuilds the trace on a fresh random path. A staggered reset (used for
	// the initial ambient population) spreads the first appearances over a
	// few seconds instead of lighting every trace up at once. With `clickAt`
	// the path is laid out from the click and then reversed, so the click is
	// the far end where the point appears and the origin lands elsewhere.
	public reset( w: number, h: number, options: TraceBackgroundOptions, bStaggered: boolean, clickAt: Point | null = null ): void {
		this.bImmediate = clickAt !== null
		this.points = generatePath( w, h, options.pureAxisShare, clickAt )
		if ( clickAt !== null ) this.points.reverse()

		this.cumulative = [ 0 ]
		for ( let i: number = 1; i < this.points.length; i++ ) {
			const d: number = Math.abs( this.points[ i ].x - this.points[ i - 1 ].x ) + Math.abs( this.points[ i ].y - this.points[ i - 1 ].y )
			this.cumulative.push( this.cumulative[ i - 1 ] + d )
		}
		this.length = this.cumulative[ this.cumulative.length - 1 ]

		const speed: number = options.speed * rand( 0.75, 1.25 )
		this.travelDuration = this.length / speed
		this.fadeInDuration = this.bImmediate ? 0.6 : rand( 1.0, 1.5 )
		this.vanishDuration = 0.5
		this.waitDuration = this.bImmediate ? 0 : ( bStaggered ? rand( 0, 10 ) : rand( 4, 12 ) )

		this.trailLength = Math.min( rand( 70, 130 ), this.length )
		this.headRadius = rand( 2.0, 2.8 )
		this.auraRadius = this.headRadius * 5.5

		this.phase = 'wait'
		this.t = 0
	}

	public isDone(): boolean {
		return this.phase === 'vanish' && this.t >= this.vanishDuration
	}

	public update( dt: number ): void {
		this.t += dt

		switch ( this.phase ) {
			case 'wait':
				if ( this.t >= this.waitDuration ) {
					this.phase = 'fadeIn'
					this.t = 0
				}
				break
			case 'fadeIn':
				if ( this.t >= this.fadeInDuration ) {
					this.phase = 'travel'
					this.t = 0
				}
				break
			case 'travel':
				if ( this.t >= this.travelDuration ) {
					this.phase = 'vanish'
					this.t = 0
				}
				break
			case 'vanish':
				break
		}
	}

	///@brief   Position on the path at arc length s from the origin.
	private pointAt( s: number ): Point {
		if ( s <= 0 ) return this.points[ 0 ]
		if ( s >= this.length ) return this.points[ this.points.length - 1 ]

		let i: number = 1
		while ( i < this.cumulative.length - 1 && this.cumulative[ i ] < s ) i++

		const a: Point = this.points[ i - 1 ]
		const b: Point = this.points[ i ]
		const segLen: number = this.cumulative[ i ] - this.cumulative[ i - 1 ]
		const f: number = segLen > 0 ? ( s - this.cumulative[ i - 1 ] ) / segLen : 0

		return { x: a.x + ( b.x - a.x ) * f, y: a.y + ( b.y - a.y ) * f }
	}

	///@brief   Path vertices from the origin up to arc length s, ending on the
	///         interpolated point at s.
	private prefix( s: number ): Point[] {
		const pts: Point[] = [ this.points[ 0 ] ]
		for ( let i: number = 1; i < this.points.length; i++ ) {
			if ( this.cumulative[ i ] >= s ) break
			pts.push( this.points[ i ] )
		}
		pts.push( this.pointAt( s ) )
		return pts
	}

	public draw( ctx: CanvasRenderingContext2D, line: RGB, glow: RGB, opacity: number ): void {
		if ( this.phase === 'wait' ) return

		const col = ( c: RGB, a: number ): string => rgba( c, a * opacity )

		let lineAlpha: number = 1
		let headAlpha: number = 0
		let s: number = this.length
		let trail: number = 0
		let vanishT: number = 0

		switch ( this.phase ) {
			case 'fadeIn': {
				const u: number = clamp01( this.t / this.fadeInDuration )
				lineAlpha = easeOutCubic( u )
				// The point only lights up once the trajectory is nearly there,
				// except under a click, where it must answer the click at once.
				headAlpha = this.bImmediate ? clamp01( u / 0.35 ) : clamp01( ( u - 0.7 ) / 0.3 )
				break
			}
			case 'travel': {
				const u: number = clamp01( this.t / this.travelDuration )
				s = this.length * ( 1 - travelEase( u ) )
				headAlpha = 1
				// The tail grows over the first moments of the run rather than
				// popping in at full length.
				trail = this.trailLength * clamp01( this.t / 0.4 )
				break
			}
			case 'vanish': {
				vanishT = clamp01( this.t / this.vanishDuration )
				s = 0
				lineAlpha = 1 - vanishT
				headAlpha = 1 - vanishT
				break
			}
		}

		const origin: Point = this.points[ 0 ]
		const head: Point = this.pointAt( s )

		ctx.lineCap = 'round'
		ctx.lineJoin = 'round'

		// Remaining trajectory: from the origin up to the point. What the point
		// has already travelled over is gone.
		if ( s > 0.5 ) {
			const pts: Point[] = this.prefix( s )

			ctx.strokeStyle = col( line, 0.5 * lineAlpha )
			ctx.lineWidth = 1.25
			ctx.beginPath()
			ctx.moveTo( pts[ 0 ].x, pts[ 0 ].y )
			for ( let i: number = 1; i < pts.length; i++ ) ctx.lineTo( pts[ i ].x, pts[ i ].y )
			ctx.stroke()

			// Waypoints at the corners still ahead of the point.
			ctx.fillStyle = col( line, 0.65 * lineAlpha )
			for ( let i: number = 1; i < pts.length - 1; i++ ) {
				ctx.beginPath()
				ctx.arc( pts[ i ].x, pts[ i ].y, 1.7, 0, Math.PI * 2 )
				ctx.fill()
			}
		}

		// Origin marker: the source the point is tracing back to.
		{
			const r: number = 3.5 + 6 * vanishT
			ctx.strokeStyle = col( line, 0.7 * lineAlpha )
			ctx.lineWidth = 1
			ctx.beginPath()
			ctx.arc( origin.x, origin.y, r, 0, Math.PI * 2 )
			ctx.stroke()
		}

		if ( headAlpha <= 0 ) return

		// Tail, ahead of the point in its direction of travel and tapering
		// toward the origin. Stacked strokes of decreasing width fake a soft
		// halo without a blur filter.
		if ( trail > 1 ) {
			const step: number = 6
			const n: number = Math.max( 2, Math.ceil( trail / step ) )
			const samples: Point[] = []
			for ( let k: number = 0; k <= n; k++ ) {
				samples.push( this.pointAt( s - ( trail * k ) / n ) )
			}

			const passes: { width: number, alpha: number, color: RGB }[] = [
				{ width: this.auraRadius * 1.5, alpha: 0.025, color: glow },
				{ width: this.auraRadius * 0.95, alpha: 0.04, color: glow },
				{ width: this.auraRadius * 0.5, alpha: 0.07, color: glow },
				{ width: this.headRadius * 1.3, alpha: 0.7, color: line },
			]

			for ( let p: number = 0; p < passes.length; p++ ) {
				const pass = passes[ p ]
				for ( let k: number = 0; k < n; k++ ) {
					const f: number = 1 - k / n
					ctx.strokeStyle = col( pass.color, pass.alpha * f * headAlpha )
					ctx.lineWidth = Math.max( 0.2, pass.width * f )
					ctx.beginPath()
					ctx.moveTo( samples[ k ].x, samples[ k ].y )
					ctx.lineTo( samples[ k + 1 ].x, samples[ k + 1 ].y )
					ctx.stroke()
				}
			}
		}

		// The point: a soft aura, a dense core and a bright centre. On the
		// vanish it flares outward while the core collapses.
		{
			const auraR: number = this.auraRadius * ( 1 + 1.2 * vanishT )
			const gradient: CanvasGradient = ctx.createRadialGradient( head.x, head.y, 0, head.x, head.y, auraR )
			gradient.addColorStop( 0, col( glow, 0.28 * headAlpha ) )
			gradient.addColorStop( 0.35, col( glow, 0.09 * headAlpha ) )
			gradient.addColorStop( 1, col( glow, 0 ) )
			ctx.fillStyle = gradient
			ctx.beginPath()
			ctx.arc( head.x, head.y, auraR, 0, Math.PI * 2 )
			ctx.fill()

			const coreR: number = this.headRadius * ( 1 - vanishT )
			if ( coreR > 0.1 ) {
				ctx.fillStyle = col( line, 0.95 * headAlpha )
				ctx.beginPath()
				ctx.arc( head.x, head.y, coreR, 0, Math.PI * 2 )
				ctx.fill()

				ctx.fillStyle = col( { r: 255, g: 255, b: 255 }, 0.9 * headAlpha )
				ctx.beginPath()
				ctx.arc( head.x, head.y, coreR * 0.42, 0, Math.PI * 2 )
				ctx.fill()
			}
		}
	}

	///@brief   Static rendering for reduced-motion: the full trajectory and
	///         its origin, no moving point.
	public drawStatic( ctx: CanvasRenderingContext2D, line: RGB, opacity: number ): void {
		ctx.lineCap = 'round'
		ctx.lineJoin = 'round'
		ctx.strokeStyle = rgba( line, 0.4 * opacity )
		ctx.lineWidth = 1.25
		ctx.beginPath()
		ctx.moveTo( this.points[ 0 ].x, this.points[ 0 ].y )
		for ( let i: number = 1; i < this.points.length; i++ ) ctx.lineTo( this.points[ i ].x, this.points[ i ].y )
		ctx.stroke()

		ctx.strokeStyle = rgba( line, 0.55 * opacity )
		ctx.lineWidth = 1
		ctx.beginPath()
		ctx.arc( this.points[ 0 ].x, this.points[ 0 ].y, 3.5, 0, Math.PI * 2 )
		ctx.stroke()
	}

}


///@brief   One canvas of traces, filling its host element.
class TraceField {

	private element: HTMLElement
	private canvas: HTMLCanvasElement
	private ctx: CanvasRenderingContext2D | null
	private options: TraceBackgroundOptions

	private lineColor: RGB
	private glowColor: RGB

	private width: number = 0
	private height: number = 0
	private dpr: number = 1

	private traces: Trace[] = []

	private bRunning: boolean = false
	private bVisible: boolean = true
	private bReducedMotion: boolean = false
	private rafId: number | null = null
	private lastFrameTime: number = 0

	private resizeObserver: ResizeObserver | null = null
	private intersectionObserver: IntersectionObserver | null = null
	private reducedMotionQuery: MediaQueryList | null = null

	constructor( element: HTMLElement, options: TraceBackgroundOptions ) {
		this.element = element
		this.options = options

		this.lineColor = parseColor( options.color, parseColor( DEFAULT_OPTIONS.color, { r: 122, g: 74, b: 144 } ) )
		this.glowColor = parseColor( options.glowColor, parseColor( DEFAULT_OPTIONS.glowColor, { r: 192, g: 112, b: 232 } ) )

		while ( element.firstChild ) element.removeChild( element.firstChild )

		this.canvas = document.createElement( 'canvas' )
		this.ctx = this.canvas.getContext( '2d' )
		element.appendChild( this.canvas )

		if ( typeof window.matchMedia === 'function' ) {
			this.reducedMotionQuery = window.matchMedia( '(prefers-reduced-motion: reduce)' )
			this.bReducedMotion = this.reducedMotionQuery.matches
			this.reducedMotionQuery.addEventListener( 'change', this )
		}

		if ( typeof ResizeObserver !== 'undefined' ) {
			this.resizeObserver = new ResizeObserver( (): void => { this.onResize() } )
			this.resizeObserver.observe( element )
		} else {
			window.addEventListener( 'resize', this )
		}

		// Only animate while the field is near the viewport. A generous margin
		// keeps a canvas sitting at the edge from flipping on and off with
		// every small scroll.
		if ( typeof IntersectionObserver !== 'undefined' ) {
			this.intersectionObserver = new IntersectionObserver( ( entries: IntersectionObserverEntry[] ): void => {
				entries.forEach( ( entry: IntersectionObserverEntry ): void => {
					this.bVisible = entry.isIntersecting
				} )
				this.syncLoop()
			}, { rootMargin: '50% 0px 50% 0px' } )
			this.intersectionObserver.observe( element )
		}

		document.addEventListener( 'visibilitychange', this )

		// The canvas lets pointer events through so the page stays usable;
		// clicks are picked up at the document level and mapped back onto it.
		if ( options.mode === 'click' ) {
			document.addEventListener( 'click', this )
		}

		this.updateSize()
	}

	handleEvent( e: Event ): void {
		switch ( e.type ) {
			case 'resize':
				this.onResize()
				break
			case 'visibilitychange':
				this.syncLoop()
				break
			case 'click':
				this.onClick( e as MouseEvent )
				break
			case 'change':
				this.bReducedMotion = this.reducedMotionQuery !== null && this.reducedMotionQuery.matches
				this.syncLoop()
				this.drawFrame()
				break
		}
	}

	private onResize(): void {
		this.updateSize()
		this.drawFrame()
	}

	private onClick( e: MouseEvent ): void {
		if ( this.bReducedMotion || document.hidden ) return

		const rect: DOMRect = this.canvas.getBoundingClientRect()
		const x: number = e.clientX - rect.left
		const y: number = e.clientY - rect.top
		if ( x < 0 || y < 0 || x > rect.width || y > rect.height ) return

		if ( this.traces.length >= MAX_CLICK_TRACES ) this.traces.shift()
		this.traces.push( new Trace( this.width, this.height, this.options, false, { x, y } ) )
		this.syncLoop()
	}

	private updateSize(): void {
		this.width = this.element.clientWidth
		this.height = this.element.clientHeight
		this.dpr = window.devicePixelRatio || 1

		this.canvas.width = Math.max( 1, Math.round( this.width * this.dpr ) )
		this.canvas.height = Math.max( 1, Math.round( this.height * this.dpr ) )
		this.canvas.style.width = this.width + 'px'
		this.canvas.style.height = this.height + 'px'

		if ( this.ctx !== null ) {
			this.ctx.setTransform( this.dpr, 0, 0, this.dpr, 0, 0 )
		}

		this.populate()
	}

	// The population is rebuilt from scratch on every resize: paths are laid
	// out against the canvas bounds, and a trace half-way through its run
	// cannot be moved without a visible jump anyway. In click mode the band
	// starts (and stays) empty until someone clicks.
	private populate(): void {
		const area: number = this.width * this.height
		const count: number = Math.round( ( area / 100000 ) * this.options.density )
		const nb: number = Math.max( MIN_TRACES, Math.min( MAX_TRACES, count ) )

		this.traces = []
		if ( this.options.mode !== 'ambient' ) return
		if ( this.width < 2 * EDGE_MARGIN + 10 || this.height < 2 * EDGE_MARGIN + 10 ) return

		for ( let i: number = 0; i < nb; i++ ) {
			this.traces.push( new Trace( this.width, this.height, this.options, true ) )
		}
	}

	// Ambient traces are recycled on a new path when done; click-fired ones
	// simply go away.
	private update( dt: number ): void {
		for ( let i: number = 0; i < this.traces.length; i++ ) {
			const trace: Trace = this.traces[ i ]
			trace.update( dt )
			if ( trace.isDone() && this.options.mode === 'ambient' ) {
				trace.reset( this.width, this.height, this.options, false )
			}
		}
		if ( this.options.mode !== 'ambient' ) {
			this.traces = this.traces.filter( ( t: Trace ): boolean => !t.isDone() )
		}
	}

	private drawFrame(): void {
		if ( this.ctx === null ) return

		this.ctx.clearRect( 0, 0, this.width, this.height )

		if ( this.bReducedMotion ) {
			for ( let i: number = 0; i < this.traces.length; i++ ) {
				this.traces[ i ].drawStatic( this.ctx, this.lineColor, this.options.opacity )
			}
			return
		}

		for ( let i: number = 0; i < this.traces.length; i++ ) {
			this.traces[ i ].draw( this.ctx, this.lineColor, this.glowColor, this.options.opacity )
		}
	}

	private animate( timestamp: number ): void {
		if ( !this.bRunning ) {
			this.rafId = null
			return
		}

		// Clamp the frame time so a tab coming back from the background does
		// not fast-forward every trace to its end in one step.
		const dt: number = Math.min( ( timestamp - this.lastFrameTime ) * 0.001, 0.05 )
		this.lastFrameTime = timestamp

		this.update( dt )
		this.drawFrame()

		// Nothing left to animate: go idle until the next click.
		if ( this.traces.length === 0 && this.options.mode !== 'ambient' ) {
			this.bRunning = false
			this.rafId = null
			return
		}

		this.rafId = window.requestAnimationFrame( ( ts: number ): void => { this.animate( ts ) } )
	}

	private syncLoop(): void {
		const bShouldRun: boolean = this.bVisible && !document.hidden && !this.bReducedMotion
		if ( bShouldRun ) {
			this.start()
		} else {
			this.stop()
		}
	}

	public start(): void {
		if ( this.bRunning ) return
		if ( !this.bVisible || document.hidden || this.bReducedMotion ) {
			this.drawFrame()
			return
		}
		this.bRunning = true
		this.lastFrameTime = performance.now()
		this.rafId = window.requestAnimationFrame( ( ts: number ): void => { this.animate( ts ) } )
	}

	public stop(): void {
		this.bRunning = false
		if ( this.rafId !== null ) {
			window.cancelAnimationFrame( this.rafId )
			this.rafId = null
		}
	}

	public destroy(): void {
		this.stop()
		if ( this.resizeObserver !== null ) this.resizeObserver.disconnect()
		if ( this.intersectionObserver !== null ) this.intersectionObserver.disconnect()
		if ( this.reducedMotionQuery !== null ) this.reducedMotionQuery.removeEventListener( 'change', this )
		window.removeEventListener( 'resize', this )
		document.removeEventListener( 'visibilitychange', this )
		document.removeEventListener( 'click', this )
		if ( this.canvas.parentNode === this.element ) this.element.removeChild( this.canvas )
	}

}


export interface TraceBackgroundProps extends Partial<TraceBackgroundOptions> {
	className?: string
}

///@brief   Decorative canvas of traces filling its positioned parent. Purely
///         visual: hidden from assistive tech and transparent to the mouse
///         (clicks reach the page underneath and fire a trace on the way).
export function TraceBackground( props: TraceBackgroundProps ): React.JSX.Element {

	const {
		className,
		mode = DEFAULT_OPTIONS.mode,
		color = DEFAULT_OPTIONS.color,
		glowColor = DEFAULT_OPTIONS.glowColor,
		density = DEFAULT_OPTIONS.density,
		pureAxisShare = DEFAULT_OPTIONS.pureAxisShare,
		speed = DEFAULT_OPTIONS.speed,
		opacity = DEFAULT_OPTIONS.opacity,
	} = props

	const ref = React.useRef<HTMLDivElement>( null )

	React.useEffect( (): ( () => void ) | undefined => {
		const element: HTMLDivElement | null = ref.current
		if ( element === null ) return undefined

		const field: TraceField = new TraceField( element, { mode, color, glowColor, density, pureAxisShare, speed, opacity } )
		field.start()

		return (): void => { field.destroy() }
	}, [ mode, color, glowColor, density, pureAxisShare, speed, opacity ] )

	return (
		<div ref={ ref } className={ `TraceBackground${ className ? ' ' + className : '' }` } aria-hidden="true" />
	)
}
